import type { QueryFunction } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { headers?: Record<string, string> }
): Promise<Response> {
  console.log('apiRequest called with:', { method, url, data });

  // Force relative URLs to use the proxy in development
  const fullUrl = url.startsWith('/') ? url : `/${url}`;
  console.log('Full URL being requested:', fullUrl);
  console.log('Current window location:', window.location.href);

  let requestBody: string | FormData | undefined;
  let headers: Record<string, string> = {};

  if (data) {
    if (data instanceof FormData) {
      // For FormData, let the browser set Content-Type with boundary
      requestBody = data;
      console.log('Request body: FormData');
    } else {
      // For JSON data
      requestBody = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
      console.log('Request body string:', requestBody);
    }
  }

  // Merge with any additional headers from options
  if (options?.headers) {
    headers = { ...headers, ...options.headers };
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: requestBody,
    credentials: 'include',
  });

  console.log('Response status:', res.status);
  console.log('Response headers:', Object.fromEntries(res.headers.entries()));

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join('/') as string;
    const res = await apiRequest('GET', url);

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
